import { inject, Injectable } from '@angular/core';
import { ReceiptLinesDocumentsFacade } from '@pf/platform-wms/domain';
import { LoggerService } from '@pf/shared-services';
import {
    BehaviorSubject,
    catchError,
    filter,
    first,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
    tap,
} from 'rxjs';
import { flatMap, isEmpty, uniq, uniqBy } from 'lodash-es';
import { DocumentDto } from '@pf/shared-common';
import { ReceiptLineMapping } from '../models/receiving-context.model';
import { PhotoCaptureFile } from '../models/photo-capture.file';

/**
 * Builds a deterministic cache key for a PO-line / ASN-shipment pair.
 * Lines without an ASN receive the `__unidentified__` suffix.
 */
export function ReceiptLineShipmentKey(
    poLineId: string,
    asnHeaderId?: string | null
) {
    return `${poLineId}-${asnHeaderId || '__unidentified__'}`.toLowerCase();
}

/** Lightweight reference to a receipt line and its parent header. */
interface ReceiptLineRef {
    receiptLineId: string;
    receiptHeaderId: string;
}

/** A shipment-key + photo pair returned by upload and load operations. */
interface ShipmentPhoto {
    shipmentKey: string;
    photo: PhotoCaptureFile;
}

/**
 * Manages shipping-documentation images (packing slips) attached to receipt lines.
 *
 * Maintains an in-memory cache (`shippingImages`) that is populated on load
 * and updated after uploads so that downstream consumers (e.g. the
 * `shipmentsToReceive$` pipeline) can reactively determine which shipment
 * keys have documentation.
 */
@Injectable()
export class ReceivingDocumentationService {
    private readonly receiptLinesDocumentsFacade = inject(
        ReceiptLinesDocumentsFacade
    );
    private readonly logger = inject(LoggerService);

    /** Cached image map; `null` until the first load completes. */
    private readonly shippingImages = new BehaviorSubject<Map<
        string,
        PhotoCaptureFile[]
    > | null>(null);

    /**
     * Emits the subset of expected shipment keys that currently have at least
     * one documentation image in the cache.
     *
     * Waits until the image cache has been populated (non-null) before emitting.
     */
    getDocumentedShipmentKeys$(
        mappings: ReceiptLineMapping[]
    ): Observable<string[]> {
        return this.shippingImages.pipe(
            filter(imageMap => imageMap !== null),
            map(imageMap => this.filterKeysWithImages(mappings, imageMap))
        );
    }

    clearShipmentDocumentationCache(): void {
        this.shippingImages.next(null);
    }

    /**
     * Uploads documentation images for one or more shipment keys, creates the
     * server-side documents, fetches preview URLs, and merges everything into
     * the local image cache.
     *
     * @returns `true` once all uploads complete and the cache is updated.
     */
    uploadShipmentDocumentation$(
        imagesByShipmentKey: Map<string, File[]>,
        mappings: ReceiptLineMapping[]
    ): Observable<boolean> {
        if (isEmpty(mappings)) {
            this.logger.warn(
                'uploadShipmentDocumentation$ called with no receipt-line mappings'
            );
            return of(true);
        }

        const uploads$ = this.buildUploadObservables(
            imagesByShipmentKey,
            mappings
        );

        if (isEmpty(uploads$)) {
            this.logger.info('No files to upload for shipment documentation');
            return of(true);
        }

        return forkJoin(uploads$).pipe(
            map(results => {
                this.mergeIntoCache(results);
                return true;
            }),
            catchError(err => {
                this.logger.error(
                    'Failed to upload shipment documentation',
                    err
                );
                return of(false);
            })
        );
    }

    /**
     * Loads all previously uploaded documentation images for the given
     * receipt-line mappings, populates the in-memory cache, and returns the
     * resulting image map.
     */
    loadShipmentDocumentation$(
        mappings: ReceiptLineMapping[]
    ): Observable<Map<string, PhotoCaptureFile[]>> {
        if (isEmpty(mappings)) {
            return of(new Map());
        }

        const uniqueLines = this.getUniqueLineRefs(mappings);

        if (isEmpty(uniqueLines)) {
            return of(new Map());
        }

        const lookups$ = uniqueLines.map(ref =>
            this.loadDocumentsForLine$(ref)
        );

        return forkJoin(lookups$).pipe(
            map(results => this.buildImageMap(flatMap(results))),
            tap(imageMap => this.shippingImages.next(imageMap)),
            catchError(err => {
                this.logger.error('Failed to load shipment documentation', err);
                const emptyImageMap = new Map<string, PhotoCaptureFile[]>();
                this.shippingImages.next(emptyImageMap);
                return of(emptyImageMap);
            })
        );
    }

    // ─── Private helpers ─────────────────────────────────────────────

    /**
     * Returns expected shipment keys that have at least one image in the map.
     */
    private filterKeysWithImages(
        mappings: ReceiptLineMapping[],
        imageMap: Map<string, PhotoCaptureFile[]>
    ): string[] {
        const expectedKeys = this.getExpectedShipmentKeys(mappings);
        return expectedKeys.filter(key => (imageMap.get(key)?.length ?? 0) > 0);
    }

    /**
     * Builds an array of upload observables — one per file × mapping combination.
     * Each observable creates the document, fetches a preview URL, and emits a
     * {@link ShipmentPhoto}.
     */
    private buildUploadObservables(
        imagesByShipmentKey: Map<string, File[]>,
        mappings: ReceiptLineMapping[]
    ): Observable<ShipmentPhoto>[] {
        const uploads$: Observable<ShipmentPhoto>[] = [];

        for (const [shipmentKey, files] of imagesByShipmentKey) {
            const lineMappings = this.findMappingsForShipment(
                mappings,
                shipmentKey
            );
            if (isEmpty(lineMappings)) {
                this.logger.warn(
                    `No receipt-line mappings found for shipment key ${shipmentKey}`
                );
            }
            for (const mapping of lineMappings) {
                for (const file of files) {
                    uploads$.push(
                        this.uploadAndPreview$(mapping, file, shipmentKey)
                    );
                }
            }
        }

        return uploads$;
    }

    /**
     * Creates a single document on the server and fetches its preview thumbnail.
     * @returns A {@link ShipmentPhoto} linking the shipment key to the uploaded photo.
     */
    private uploadAndPreview$(
        mapping: ReceiptLineMapping,
        file: File,
        shipmentKey: string
    ): Observable<ShipmentPhoto> {
        return this.receiptLinesDocumentsFacade
            .create$(
                mapping.receiptLineId,
                this.buildDocumentDto(file, shipmentKey),
                mapping.receiptHeaderId
            )
            .pipe(
                switchMap(doc =>
                    this.fetchPreviewAsPhoto$(mapping, doc, file, shipmentKey)
                ),
                catchError(err => {
                    this.logger.error(
                        `Failed to upload document for shipment key ${shipmentKey} ` +
                            `(receiptLine=${mapping.receiptLineId})`,
                        err
                    );
                    throw err;
                })
            );
    }

    /**
     * Fetches the preview URL for an uploaded document and wraps it in a
     * {@link ShipmentPhoto}.
     */
    private fetchPreviewAsPhoto$(
        mapping: ReceiptLineMapping,
        doc: { id: string; fileName?: string },
        file: File,
        shipmentKey: string
    ): Observable<ShipmentPhoto> {
        return this.receiptLinesDocumentsFacade
            .previewUrl$(mapping.receiptLineId, doc.id, mapping.receiptHeaderId)
            .pipe(
                map(thumbUrl => ({
                    shipmentKey,
                    photo: {
                        uid: doc.id,
                        name: doc.fileName ?? 'document',
                        thumbUrl,
                        file,
                    },
                }))
            );
    }

    /**
     * Merges newly uploaded photos into the existing image cache,
     * de-duplicating by `uid`.
     */
    private mergeIntoCache(newPhotos: ShipmentPhoto[]): void {
        const uploaded = this.buildImageMap(newPhotos);
        const current = this.shippingImages.getValue() ?? new Map();

        for (const [key, existingPhotos] of current) {
            if (existingPhotos.length === 0) continue;

            if (!uploaded.has(key)) {
                uploaded.set(key, existingPhotos);
            } else {
                const merged = uniqBy(
                    [
                        ...(uploaded.get(key) as PhotoCaptureFile[]),
                        ...existingPhotos,
                    ],
                    'uid'
                );
                uploaded.set(key, merged);
            }
        }

        this.shippingImages.next(uploaded);
    }

    /** Deduplicates mappings down to unique receipt-line references. */
    private getUniqueLineRefs(
        mappings: ReceiptLineMapping[]
    ): ReceiptLineRef[] {
        return uniqBy(
            mappings.map(m => ({
                receiptLineId: m.receiptLineId,
                receiptHeaderId: m.receiptHeaderId,
            })),
            'receiptLineId'
        ) as ReceiptLineRef[];
    }

    /**
     * Loads all documents attached to a single receipt line and resolves each
     * to a {@link ShipmentPhoto} with a preview thumbnail URL.
     */
    private loadDocumentsForLine$(
        ref: ReceiptLineRef
    ): Observable<ShipmentPhoto[]> {
        return this.receiptLinesDocumentsFacade
            .all$(ref.receiptLineId, ref.receiptHeaderId)
            .pipe(
                first(),
                switchMap(docs => this.resolveDocumentPreviews$(ref, docs)),
                catchError(err => {
                    this.logger.error(
                        `Failed to load documents for receipt line ${ref.receiptLineId}`,
                        err
                    );
                    return of([]);
                })
            );
    }

    /**
     * Fetches preview URLs for an array of existing documents and maps each
     * to a {@link ShipmentPhoto}.
     */
    private resolveDocumentPreviews$(
        ref: ReceiptLineRef,
        docs: DocumentDto[]
    ): Observable<ShipmentPhoto[]> {
        if (isEmpty(docs)) {
            return of([]);
        }

        return forkJoin(
            docs.map(doc =>
                this.receiptLinesDocumentsFacade
                    .previewUrl$(ref.receiptLineId, doc.id, ref.receiptHeaderId)
                    .pipe(
                        map(thumbUrl => ({
                            shipmentKey: doc.description as string,
                            photo: {
                                uid: doc.id,
                                name: doc.fileName ?? 'document',
                                thumbUrl,
                                file: new File([], doc.fileName ?? 'document'),
                            } as PhotoCaptureFile,
                        }))
                    )
            )
        );
    }

    /** Groups an array of {@link ShipmentPhoto}s into a map keyed by shipment key. */
    private buildImageMap(
        entries: ShipmentPhoto[]
    ): Map<string, PhotoCaptureFile[]> {
        const imageMap = new Map<string, PhotoCaptureFile[]>();
        for (const { shipmentKey, photo } of entries) {
            const existing = imageMap.get(shipmentKey) ?? [];
            existing.push(photo);
            imageMap.set(shipmentKey, existing);
        }
        return imageMap;
    }

    /** Derives the full set of expected shipment keys from receipt-line mappings. */
    private getExpectedShipmentKeys(mappings: ReceiptLineMapping[]): string[] {
        return uniq(
            mappings.map(m => ReceiptLineShipmentKey(m.poLineId, m.asnHeaderId))
        );
    }

    /** Returns mappings whose computed shipment key matches the given key. */
    private findMappingsForShipment(
        mappings: ReceiptLineMapping[],
        shipmentKey: string
    ): ReceiptLineMapping[] {
        return mappings.filter(
            m =>
                ReceiptLineShipmentKey(m.poLineId, m.asnHeaderId) ===
                shipmentKey
        );
    }

    /** Constructs a {@link DocumentDto} for a packing-slip upload. */
    private buildDocumentDto(file: File, shipmentKey: string): DocumentDto {
        return new DocumentDto({
            file,
            fileName: file.name,
            documentTypeId: '',
            documentTypeName: 'Packing Slip',
            description: shipmentKey,
        });
    }
}
